/**
 * Client mirror of the server sport definitions (specs/multi-sport-core).
 *
 * Must match `server/src/sports/definitions/{football,basketball}.js`.
 * Verified field by field by `src/sports/__tests__/sportsParity.test.ts` — see the note in
 * `sports.types.ts` for why a test rather than a comment.
 *
 * Only the fields the client actually needs are mirrored. Server-only concerns (live windows,
 * market catalogues, standings formulas) are deliberately absent: mirroring them would create
 * drift surface for no benefit.
 */

import type { SportDefinition, SportKey } from './sports.types';

export const SPORT_KEYS = {
  FOOTBALL: 'FOOTBALL',
  BASKETBALL: 'BASKETBALL',
  FORMULA_ONE: 'FORMULA_ONE',
} as const;

/** The sport every pre-existing league, team and match belongs to. */
export const DEFAULT_SPORT: SportKey = SPORT_KEYS.FOOTBALL;

export const OUTCOME_SHAPES = {
  HEAD_TO_HEAD: 'HEAD_TO_HEAD',
  FIELD: 'FIELD',
} as const;

export const IDENTITY_MODES = {
  KIT: 'KIT',
  LOGO: 'LOGO',
} as const;

/** Football standings columns, in display order. */
export const FOOTBALL_STANDINGS_COLUMNS = [
  'played', 'won', 'drawn', 'lost', 'goalsFor', 'goalsAgainst', 'goalDiff', 'points',
] as const;

/** Basketball standings columns — no draws, no goals, no points-for-a-win. */
export const BASKETBALL_STANDINGS_COLUMNS = [
  'played', 'won', 'lost', 'winPct', 'gamesBehind',
] as const;

/**
 * Formula One standings columns — a drivers' championship, not a league table: no played/won/lost,
 * because a driver does not play against one opponent.
 */
export const FORMULA_ONE_STANDINGS_COLUMNS = ['wins', 'points', 'behind'] as const;

const FOOTBALL: SportDefinition = {
  key: SPORT_KEYS.FOOTBALL,
  label: 'Football',
  shape: OUTCOME_SHAPES.HEAD_TO_HEAD,
  scoreUnit: 'goals',
  fixtureNoun: { one: 'match', many: 'matches' },
  allowsDraw: true,
  periods: { count: 2, label: 'Half', shortLabel: 'H', supportsOvertime: true },
  maxPredictedScore: 10,
  settlesOnRegulation: true,
  identityMode: IDENTITY_MODES.KIT,
  standingsColumns: FOOTBALL_STANDINGS_COLUMNS,
  // 1X2. Mirrors MARKET_IDS.MATCH_WINNER on the server, and the name must stay byte-identical to
  // MATCH_WINNER_MARKET_NAME / the server catalogue: `marketName` is persisted on ChallengePick
  // and rendered, so changing it would silently relabel every football pick.
  primaryMarketId: 1,
  primaryMarketName: 'Match Winner (1X2)',
  primaryMarketHasDraw: true,
};

const BASKETBALL: SportDefinition = {
  key: SPORT_KEYS.BASKETBALL,
  label: 'Basketball',
  shape: OUTCOME_SHAPES.HEAD_TO_HEAD,
  scoreUnit: 'points',
  fixtureNoun: { one: 'game', many: 'games' },
  allowsDraw: false,
  periods: { count: 4, label: 'Quarter', shortLabel: 'Q', supportsOvertime: true },
  maxPredictedScore: 200,
  settlesOnRegulation: false,
  identityMode: IDENTITY_MODES.LOGO,
  standingsColumns: BASKETBALL_STANDINGS_COLUMNS,
  // Regulation Home/Draw/Away — basketball's headline market, shown before the list is expanded
  // (specs/basketball-regulation-1x2). Mirrors BASKETBALL_MARKET_IDS.REGULATION_1X2. A basketball
  // pick must NEVER carry market 1: that is football's 1X2 and no basketball resolver handles it.
  primaryMarketId: 106,
  primaryMarketName: 'Match Winner (Regulation)',
  // TRUE even though `allowsDraw` is false: this market settles on REGULATION, where a tie is a
  // real 6-8% outcome. `allowsDraw` answers a different question — can the FINAL score be level —
  // and must stay false so the score-prediction pool keeps rejecting ties.
  primaryMarketHasDraw: true,
};

/**
 * The first FIELD sport (specs/formula-one-challenges).
 *
 * `maxPredictedScore: 0` is not a placeholder: score prediction is not offered for a field sport,
 * and zero keeps the value a number while making any score-prediction path that reaches it reject
 * every score rather than accept any.
 */
const FORMULA_ONE: SportDefinition = {
  key: SPORT_KEYS.FORMULA_ONE,
  label: 'Formula 1',
  shape: OUTCOME_SHAPES.FIELD,
  scoreUnit: 'points',
  fixtureNoun: { one: 'race', many: 'races' },
  allowsDraw: false,
  periods: { count: 1, label: 'Race', shortLabel: 'R', supportsOvertime: false },
  maxPredictedScore: 0,
  settlesOnRegulation: false,
  identityMode: IDENTITY_MODES.LOGO,
  standingsColumns: FORMULA_ONE_STANDINGS_COLUMNS,
  // Mirrors FORMULA_ONE_MARKET_IDS.RACE_WINNER. A race pick must never carry market 1 or 106 —
  // those are football's and basketball's, and no F1 resolver handles either.
  primaryMarketId: 200,
  primaryMarketName: 'Race Winner',
  primaryMarketHasDraw: false,
};

const DEFINITIONS: Record<SportKey, SportDefinition> = {
  FOOTBALL,
  BASKETBALL,
  FORMULA_ONE,
};

/**
 * Resolve a sport key to its definition.
 *
 * Returns null for an unknown key rather than falling back to football — the same rule the
 * server registry follows, so a competition with a bad `sport` fails visibly instead of
 * rendering football's rules over another sport's data.
 */
export function getSportDefinition(sportKey: string | null | undefined): SportDefinition | null {
  if (typeof sportKey !== 'string') return null;
  return DEFINITIONS[sportKey as SportKey] ?? null;
}

/**
 * Definition for a sport, falling back to football when the key is absent.
 *
 * Use where the sport is optional in the payload (older server, older cached response) and
 * football is the correct assumption. Where an unknown sport must be surfaced as an error,
 * use {@link getSportDefinition} and handle null.
 */
export function getSportDefinitionOrDefault(sportKey: string | null | undefined): SportDefinition {
  return getSportDefinition(sportKey) ?? DEFINITIONS[DEFAULT_SPORT];
}

export function listSportKeys(): SportKey[] {
  return Object.keys(DEFINITIONS) as SportKey[];
}

export { DEFINITIONS as SPORT_DEFINITIONS };
